import React from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface InfoTipsPopupProps {
  isVisible: boolean;
  onClose: () => void;
}

const InfoTipsPopup: React.FC<InfoTipsPopupProps> = ({ isVisible, onClose }) => {
  const { t } = useTranslation('tips');
  const cardRef = React.useRef<HTMLDivElement>(null);

  // Close when clicking anywhere outside the modal (including areas not covered by the semi-transparent backdrop)
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, onClose]);
  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop - transparent overlay to capture all clicks outside modal */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
        style={{ pointerEvents: 'all' }}
      />
      
      {/* Modal */}
      <div className="fixed inset-x-0 top-28 z-50 flex justify-center p-2 sm:p-4">
        <div ref={cardRef} className="bg-white info-popup-container rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] sm:max-h-[80vh] overflow-y-auto mx-2 sm:mx-0">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 info-popup-header sticky top-0 bg-white z-10">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 info-popup-title">{t('tips.title')}</h2>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 info-popup-close-btn transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            <p className="text-gray-700 info-popup-text leading-relaxed">
              {t('tips.intro')}
            </p>
            
            {/* Ask One Question at a Time */}
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-gray-900 info-popup-section-title">{t('tips.one_question_title')}</h3>
              <p className="text-gray-700 info-popup-text leading-relaxed">
                {t('tips.one_question_desc')}
              </p>
              <div className="bg-gray-50 info-popup-example-box p-4 rounded-lg space-y-2">
                <p className="text-sm text-gray-600 info-popup-example-text">
                  <span className="font-medium text-red-600">{t('tips.one_question_bad')}</span> "Tell me everything thats described about the Universe in the Yoga Vasistha. Also tell me all about timescales like Yuga"
                </p>
                <p className="text-sm text-gray-600 info-popup-example-text">
                  <span className="font-medium text-green-600">{t('tips.one_question_good')}</span>
                </p>
                <ul className="text-sm text-gray-600 info-popup-example-text ml-4 space-y-1">
                  <li>"What does the Yoga Vasistha say about the nature of the universe?"</li>
                  <li>(After getting the answer) "Now, tell me what the Yoga Vasistha says about timescales like Yugas."</li>
                </ul>
              </div>
            </div>
            
            {/* Be Specific Instead of General */}
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-gray-900 info-popup-section-title">{t('tips.specific_title')}</h3>
              <p className="text-gray-700 info-popup-text leading-relaxed">
                {t('tips.specific_desc')}
              </p>
              <div className="bg-gray-50 info-popup-example-box p-4 rounded-lg space-y-2">
                <p className="text-sm text-gray-600 info-popup-example-text">
                  <span className="font-medium text-red-600">Instead of:</span> "Rig Veda"
                </p>
                <p className="text-sm text-gray-600 info-popup-example-text">
                  <span className="font-medium text-green-600">Try asking:</span> "What are the ethical teachings of the Rigveda?" or "Tell me the story of creation from the Nasadiya Sukta."
                </p>
              </div>
            </div>
            
            {/* Provide Context for Specific Searches */}
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-gray-900 info-popup-section-title">{t('tips.context_title')}</h3>
              <p className="text-gray-700 info-popup-text leading-relaxed">
                {t('tips.context_desc')}
              </p>
              <div className="bg-gray-50 info-popup-example-box p-4 rounded-lg space-y-2">
                <p className="text-sm text-gray-600 info-popup-example-text">
                  <span className="font-medium text-red-600">Instead of:</span> "What do scriptures say about yogis?"
                </p>
                <p className="text-sm text-gray-600 info-popup-example-text">
                  <span className="font-medium text-green-600">Try asking:</span> "Find the shloka in the Ashtavakra Gita about a yogi being unaffected by life or death."
                </p>
              </div>
            </div>
            
            {/* Ask for Comparisons Directly */}
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-gray-900 info-popup-section-title">{t('tips.comparison_title')}</h3>
              <p className="text-gray-700 info-popup-text leading-relaxed">
                {t('tips.comparison_desc')}
              </p>
              <div className="bg-gray-50 info-popup-example-box p-4 rounded-lg space-y-2">
                <p className="text-sm text-gray-600 info-popup-example-text">
                  <span className="font-medium text-red-600">Instead of:</span> "Tell me about Shiva and Vishnu."
                </p>
                <p className="text-sm text-gray-600 info-popup-example-text">
                  <span className="font-medium text-green-600">Try asking:</span> "What is the difference between Shiva and Vishnu according to the Puranas?" or "Compare the concepts of Advaita and Vishishtadvaita."
                </p>
              </div>
            </div>
            
            {/* Use Clear Action Words */}
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-gray-900 info-popup-section-title">{t('tips.action_title')}</h3>
              <p className="text-gray-700 info-popup-text leading-relaxed">
                {t('tips.action_desc')}
              </p>
              <div className="bg-gray-50 info-popup-example-box p-4 rounded-lg">
                <p className="text-sm text-gray-600 info-popup-example-text">
                  <span className="font-medium text-green-600">Try starting with:</span> "Explain...", "Tell me the story of...", "What are the benefits of...", or "List the rules for...".
                </p>
              </div>
            </div>
            
            {/* Prioritize Traditional Sources with Filters */}
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-gray-900 info-popup-section-title">{t('tips.filter_title')}</h3>
              <p className="text-gray-700 info-popup-text leading-relaxed">
                {t('tips.filter_desc')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InfoTipsPopup;
